import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { TextInput, Button } from "flowbite-react";
import { useGetData } from "../../../../../hooks/useGetData";
import { EvaluadoInterface } from "../../../../../interfaces/EvaluadoInterface";
import { FechaFormatosGeneralesInterface } from "../../../../../interfaces/FechaFormatosGeneralesInterface";
import { ConstanciaInasistenciaInterface } from "../../../../../interfaces/ConstanciaInasistenciaInterface";
import HeaderPrint from "../../../../../components/HeaderFormsPrint/HeaderFormsPrint";
import fotoMapa from "../../../../../img/fotoMapa.png";

export const ConstanciaInasistenciaListaPrint = () => {
  const tituloFormulario = "CONSTANCIA DE INASISTENCIA";
  const [formData, setFormData] = useState({
    day: "",
    month: "",
    year: "",
  });

  const { evaluadosList_id } = useParams<{ evaluadosList_id: string }>();
  const { control, handleSubmit, setValue, reset } = useForm();

  const queryEvaluado = useGetData<EvaluadoInterface>({
    url: `/evaluado/${evaluadosList_id}`,
    name: ["evaluado"],
  });

  const queryFechaFG = useGetData<FechaFormatosGeneralesInterface>({
    url: `/fecha_formatosgenerales/${evaluadosList_id}`,
    name: ["evaluado", `${evaluadosList_id}`, "fecha_formatosgenerales"],
  });

  const queryConstanciaInasistencia =
    useGetData<ConstanciaInasistenciaInterface>({
      url: `/constancia_inasistencia/${evaluadosList_id}`,
      name: ["evaluado", `${evaluadosList_id}`, "constancia_inasistencia"],
    });

  useEffect(() => {
    if (queryFechaFG.data) {
      const fechafg = queryFechaFG.data;
      reset({
        fecha_inasistencia: fechafg.fecha_inasistencia || "",
      });
    }
  }, [queryFechaFG.data, reset]);

  useEffect(() => {
    if (queryConstanciaInasistencia.data) {
      const constanciaI = queryConstanciaInasistencia.data;
      reset({
        fecha_programada_inasistencia:
          constanciaI.fecha_programada_inasistencia || "",
        empresanotifico_ina: constanciaI.empresanotifico_ina || "",
        evaluacion_ina: constanciaI.evaluacion_ina || "",
        integrantes_empresa_ina: constanciaI.integrantes_empresa_ina || "",
      });
    }
  }, [queryConstanciaInasistencia.data, reset]);

  return (
    <div className="max-w-4xl mx-auto bg-white shadow-lg rounded-lg overflow-hidden mt-4 mb-4 relative">
      {/* Header */}
      <HeaderPrint titulo={tituloFormulario} />
      {/* Título del formulario */}
      <div id="headerContent" className="text-center">
        <h1
          className="text-black text-xl font-bold py-3 px-8 rounded-b-md inline-block flex justify-center transition-transform duration-300 ease-in-out hover:scale-105 cursor-pointer"
          style={{
            backgroundImage: `url(${fotoMapa})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          {tituloFormulario}
        </h1>
      </div>
      {/* Contenido del formulario */}
      <div className="p-6">
        <div className="text-center mb-8">
          <div className="flex justify-left items-center gap-2 mb-4">
            <p className="mt-3">Ciudad de México a</p>
            <span>
              {queryFechaFG.data?.fecha_inasistencia
                ? (() => {
                    const date = new Date(queryFechaFG.data.fecha_inasistencia);
                    date.setDate(date.getDate() + 1);
                    return date.toLocaleDateString("es-ES", {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                    });
                  })()
                : "Fecha no disponible"}
            </span>
          </div>
          <h2 className="text-xl font-bold mb-4 flex justify-center items-center">
            CONSTANCIA DE INASISTENCIA
          </h2>
        </div>

        <div className="flex flex-col items-start p-6 space-y-6 relative">
          <p className="text-justify text-gray-800 leading-relaxed">
            <label htmlFor="nombre" className="text-gray-800 inline-block">
              <strong>SE HACE CONSTAR </strong>Que el C.{" "}
              {`${queryEvaluado.data?.nombre || ""} ${
                queryEvaluado.data?.apellido_paterno || ""
              } ${queryEvaluado.data?.apellido_materno || ""}`}
            </label>{" "}
            <label
              htmlFor="nombre"
              className="text-gray-800 font-medium inline-block underline"
            >
              NO ASISTIÓ
            </label>{" "}
            a sus evaluaciones de Pruebas de Confianza, correspondiente al día{" "}
            {queryConstanciaInasistencia.data?.fecha_programada_inasistencia
              ? (() => {
                  const date = new Date(
                    queryConstanciaInasistencia.data.fecha_programada_inasistencia
                  );
                  date.setDate(date.getDate() + 1);
                  return date.toLocaleDateString("es-ES", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                  });
                })()
              : "Fecha no disponible"}
            , a pesar de haber sido notificado por la empresa{" "}
            {queryConstanciaInasistencia.data?.empresanotifico_ina}, para
            someterse y practicarse las evaluaciones orientadas a cumplir los
            requisitos para la evaluación de{" "}
            {queryConstanciaInasistencia.data?.evaluacion_ina}, como integrantes
            de la empresa{" "}
            {queryConstanciaInasistencia.data?.integrantes_empresa_ina}, lo
            anterior se desprende del minucioso análisis de los archivos que se
            encuentran bajo el resguardo, derivándose lo siguiente:
          </p>
          <table className="w-full border border-gray-300 text-left text-sm">
            <thead>
              <tr className="bg-gray-200">
                <th className="border border-gray-400 px-4 py-2">Campo</th>
                <th className="border border-gray-400 px-4 py-2">
                  Información
                </th>
              </tr>
            </thead>
            <tbody>
              <tr className="bg-white">
                <td className="border border-gray-400 px-4 py-2 font-medium">
                  NOMBRE DE LA EMPRESA
                </td>
                <td className="border border-gray-400 px-4 py-2">
                  {`${queryEvaluado.data?.empresa_id?.nombre || ""}`}
                </td>
              </tr>
              <tr className="bg-white">
                <td className="border border-gray-400 px-4 py-2 font-medium">
                  NOMBRE DEL EVALUADO
                </td>
                <td className="border border-gray-400 px-4 py-2">{`${
                  queryEvaluado.data?.nombre || ""
                } ${queryEvaluado.data?.apellido_paterno || ""} ${
                  queryEvaluado.data?.apellido_materno || ""
                }`}</td>
              </tr>
              <tr className="bg-white">
                <td className="border border-gray-400 px-4 py-2 font-medium">
                  RFC
                </td>
                <td className="border border-gray-400 px-4 py-2">
                  {`${queryEvaluado.data?.rfc || ""}`}
                </td>
              </tr>
              <tr className="bg-white">
                <td className="border border-gray-400 px-4 py-2 font-medium">
                  CARGO
                </td>
                <td className="border border-gray-400 px-4 py-2">
                  {`${queryEvaluado.data?.puesto || ""}`}
                </td>
              </tr>
              <tr className="bg-white">
                <td className="border border-gray-400 px-4 py-2 font-medium">
                  FECHA DE PROGRAMACIÓN
                </td>
                <td className="border border-gray-400 px-4 py-2">
                  {queryConstanciaInasistencia.data
                    ?.fecha_programada_inasistencia
                    ? (() => {
                        const date = new Date(
                          queryConstanciaInasistencia.data.fecha_programada_inasistencia
                        );
                        date.setDate(date.getDate() + 1);
                        return date.toLocaleDateString("es-ES", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                        });
                      })()
                    : "Fecha no disponible"}
                </td>
              </tr>
              <tr className="bg-white">
                <td className="border border-gray-400 px-4 py-2 font-medium">
                  EVALUACIONES PROGRAMADAS
                </td>
                <td className="border border-gray-400 px-4 py-2">
                  {queryConstanciaInasistencia.data?.evaluacion_ina}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    <div className="mt-[100px]"></div>
      {/* Footer */}
      <div className="bg-customBlue/90 py-2 rounded-b-lg w-full">
        <p className="text-white text-xs text-center font-light px-4">
          <span className="font-medium">DOCUMENTO CONFIDENCIAL</span> •
          Prohibida su reproducción o copia fuera de las instalaciones de
          Skyangel.
        </p>
      </div>
    </div>
  );
};
